import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, ScanText } from 'lucide-react';
import { Badge } from '../ui';
import { apiClient } from '../../api/client';
import { classifyError } from './errors';
import PanelState from './PanelState';

/**
 * Normalises the two document backends that already exist into one list:
 * Axiom (`/api/axiom/*`, NyxCore RAG) and Catalog (`/api/catalog/imports`,
 * the PDF/image extraction pipeline). Ingestible has no HTTP surface yet, so
 * it is deliberately absent rather than faked.
 */
async function loadDocuments() {
  const [axiom, catalog] = await Promise.allSettled([
    apiClient.request('/axiom/documents'),
    apiClient.request('/catalog/imports')
  ]);

  const documents = [];
  let axiomError = null;

  if (axiom.status === 'fulfilled') {
    for (const doc of axiom.value?.documents || []) {
      documents.push({
        id: doc.id,
        filename: doc.filename || doc.title || String(doc.id),
        authority: doc.authority,
        category: doc.category,
        status: doc.status,
        chunkCount: doc.chunkCount ?? doc.chunk_count,
        source: 'axiom'
      });
    }
  } else {
    axiomError = axiom.reason;
  }

  if (catalog.status === 'fulfilled') {
    for (const row of catalog.value?.imports || []) {
      documents.push({
        id: row.id,
        filename: row.filename || row.title || String(row.id),
        category: row.title,
        status: row.status,
        chunkCount: row.total_pages,
        source: 'catalog'
      });
    }
  }

  // Only surface a hard failure when *both* backends failed; a tenant with
  // Axiom disabled but catalog imports present is a normal, working state.
  if (documents.length === 0 && axiomError && catalog.status === 'rejected') {
    throw axiomError;
  }

  return documents;
}

/**
 * The document side of the knowledge explorer (spec §9.5 "Dokumente").
 *
 * @param {object} props
 * @param {string} [props.query] - shared explorer search term.
 * @param {string|null} [props.selectedId]
 * @param {(document: object) => void} props.onSelect
 */
export function DocumentLibrary({ query = '', selectedId = null, onSelect }) {
  const { t } = useTranslation();
  const [documents, setDocuments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadDocuments()
      .then((result) => {
        if (!cancelled) setDocuments(result);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err);
          setDocuments([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => load(), [load]);

  const needle = query.trim().toLowerCase();
  const visible = useMemo(
    () =>
      (documents || []).filter(
        (doc) =>
          !needle ||
          doc.filename.toLowerCase().includes(needle) ||
          String(doc.category || '').toLowerCase().includes(needle)
      ),
    [documents, needle]
  );

  if (loading) return <PanelState status="loading" compact />;
  if (error) {
    return (
      <PanelState
        status={classifyError(error)}
        error={error}
        description={t('knowledge.documentsUnavailable')}
        onRetry={load}
        compact
      />
    );
  }
  if ((documents || []).length === 0) {
    return <PanelState status="noSource" description={t('knowledge.noDocumentsHint')} onRetry={load} compact />;
  }
  if (visible.length === 0) {
    return <p className="etch-knowledge__rail-empty">{t('knowledge.noSearchResults')}</p>;
  }

  return (
    <ul className="etch-knowledge__list" aria-label={t('knowledge.documents')}>
      {visible.map((doc) => {
        const selected = String(doc.id) === String(selectedId);
        const Icon = doc.source === 'catalog' ? ScanText : FileText;
        return (
          <li key={`${doc.source}-${doc.id}`}>
            <button
              type="button"
              className="etch-knowledge__list-item"
              aria-current={selected ? 'true' : undefined}
              onClick={() => onSelect?.(doc)}
            >
              <span className="etch-knowledge__list-icon" aria-hidden="true">
                <Icon size={15} />
              </span>
              <span className="etch-knowledge__list-body">
                <span className="etch-knowledge__list-title">{doc.filename}</span>
                <span className="etch-knowledge__list-meta">
                  {t(doc.source === 'catalog' ? 'knowledge.sourceCatalog' : 'knowledge.sourceAxiom')}
                  {doc.chunkCount ? ` · ${t('knowledge.chunks', { count: doc.chunkCount })}` : ''}
                </span>
              </span>
              {doc.authority && <Badge tone="brand">{doc.authority}</Badge>}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export default DocumentLibrary;
